import { Logger, NOOP_PROMISE_VOID, Presenter } from 'wdc-cube'

import { WaitingScope } from '../../the-connection/tc_scopes'
import ConfigurationPresenter from './presenter'
import { ManageTypesScope } from './scopes'

import { NotificationType } from '../user-manager/um_scopes'
import { NotificationTypesManagerService } from './notification-types-manager.service'

const LOG = Logger.get('ManageTypesPresenter')
const service = NotificationTypesManagerService.singleton()

export class ManageTypesPresenter extends Presenter<ManageTypesScope, ConfigurationPresenter> {
    // :: Constructor

    public constructor(owner: ConfigurationPresenter) {
        super(owner, owner.scope.notificationTab.manageTypeScope)
        this.__parent = owner
    }

    // :: Instance

    private readonly __parent: ConfigurationPresenter

    private _yesAction = NOOP_PROMISE_VOID

    // :: Methods

    public override release() {
        super.release()
    }

    async initialize() {
        LOG.debug('initialize')

        this.scope.onNameChange = this.action(this.handleNameChange)
        this.scope.onActiveChange = this.action(this.handleActiveChange)
        this.scope.onSave = this.action(this.handleSave)
        this.scope.onOpen = this.action(this.handleOpen)
        this.scope.onClose = this.action(this.handleClose)
        this.scope.onNew = this.action(this.handleNew)
        this.scope.onEdit = this.action(this.handleEdit)
        this.scope.onDelete = this.action(this.handleDelete)
        this.scope.onYesNoResponse = this.action(this.handleYesNoResponse)

        this.handleFind().catch(LOG.caught)
    }

    private async handleFind() {
        try {
            this.scope.waiting = new WaitingScope()
            const rows = await service.fetchAll(false)
            this.scope.allNotificationTypes = rows
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleEdit(typeId: number) {
        const type = this.scope.allNotificationTypes.find((t) => Number(t.id) === typeId)
        this.scope.typeId = type?.id || 0
        this.scope.name = type?.name || ''
        this.scope.active = !!type?.active
    }

    private async handleDelete(typeId: number) {
        this.scope.askYesOrNo = 'Deseja excluir o tipo de notificação?'
        this._yesAction = async () => {
            try {
                await service.delete(typeId)
                await this.handleFind()
                this._yesAction = NOOP_PROMISE_VOID
                this.__parent.app.alert('success', 'Sucesso', 'Tipo de notificação excluído com sucesso')
            } catch (error) {
                LOG.caught(error)
                this.__parent.app.alert(
                    'error',
                    'Problemas',
                    'Não foi possível excluir o tipo de notificação, ele está vinculado a alguma notificação.'
                )
            }
        }
    }

    protected async handleYesNoResponse(yes: boolean) {
        LOG.debug(`handYesNoResponse: yes=${yes}`)
        this.scope.askYesOrNo = ''
        if (yes) {
            await this._yesAction()
        }
        this.scope.update()
    }

    private async handleNew() {
        this.scope.typeId = 0
        this.scope.name = ''
        this.scope.active = true
    }

    private async handleClose() {
        this.scope.manageTypesOpen = false
        await this.__parent.scope.notificationTab.onTypesChanged()
    }

    private async handleOpen() {
        this.scope.manageTypesOpen = true
    }

    private async handleNameChange(name: string) {
        this.scope.name = name
    }

    private async handleActiveChange(active: boolean) {
        this.scope.active = active
    }

    private async handleSave() {
        try {
            if (!this.scope.name) {
                this.__parent.app.notify('warning', 'Nome do tipo da notificação deve ser informado')
                return
            }

            const formData = {
                name: this.scope.name,
                active: this.scope.active
            } as NotificationType

            const retSalvar = this.scope.typeId
                ? await service.update(this.scope.typeId, formData)
                : await service.create(formData)

            if (retSalvar) {
                this.action(this.handleNew)()
                await this.handleFind()
                this.__parent.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }
}

export default ManageTypesPresenter
